// Shared live-pick list for tennis surfaces — the clickable match rows + the
// Fair Price drawer. Used by both the per-product screen (TennisProductScreen)
// and the Signals page's live Full-Draw feed so they stay in lockstep.
//
// Renders only what the engine published (honest, never padded). A row opens the
// Fair Price drawer: our model read + every market we priced for that match.
import { useState } from "react";
import { Badge, Kicker } from "../_kit.tsx";
import type { TennisPick, TennisPickMarket } from "./useTennisProduct.ts";
import { formatPick, formatSelection, marketLabel } from "./pick-format.ts";

function fmtOdds(o: number | null): string {
  return o != null ? o.toFixed(2) : "—";
}
function fmtPct(p: number | null): string {
  return p != null ? `${Math.round(p * 100)}%` : "—";
}

// One real leg/pick row. Missing fields simply don't render.
export function PickRow({ pick, onOpen }: { pick: TennisPick; onOpen: (p: TennisPick) => void }): JSX.Element {
  const tier = pick.tier ?? pick.confidence ?? null;
  const showEdge = pick.tier !== "READ" && pick.edge_pct != null;
  const hasOdds = pick.book_odds != null;
  return (
    <div
      className="tn-acca__leg tn-acca__leg--clickable"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(pick)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(pick); } }}
      style={{ cursor: "pointer" }}
    >
      <span className="tn-acca__leg-main">
        <span className="tn-acca__leg-match">{pick.match_name || "—"}</span>
        <span className="tn-acca__leg-sel">{formatPick(pick.market, pick.selection, pick.match_name)}</span>
      </span>
      <span className="tn-acca__leg-meta">
        {tier ? <span className="tn-ghost__tag">{tier}</span> : null}
        {pick.fair_prob != null ? <span className="tn-ghost__tag">model {Math.round(pick.fair_prob * 100)}%</span> : null}
        {showEdge ? <span className="tn-acca__leg-edge">+{pick.edge_pct!.toFixed(1)}%</span> : null}
        {pick.clv_pct != null ? (
          <span
            className="tn-ghost__tag"
            title={pick.entry_odds != null ? `Line moved from ${pick.entry_odds.toFixed(2)} → ${fmtOdds(pick.book_odds)}` : "Closing-line value vs our entry price"}
            style={{ color: pick.clv_pct >= 0 ? "var(--green, #3fb950)" : "var(--text-secondary)" }}
          >
            CLV {pick.clv_pct >= 0 ? "+" : ""}{pick.clv_pct.toFixed(1)}%
          </span>
        ) : null}
        <span className="tn-acca__leg-odds">{hasOdds ? fmtOdds(pick.book_odds) : "no line"}</span>
        <span className="tn-ghost__tag" aria-hidden>›</span>
      </span>
    </div>
  );
}

// Fair Price drawer — opens when a match row is clicked.
export function FairPriceDrawer({ pick, onClose }: { pick: TennisPick; onClose: () => void }): JSX.Element {
  const markets = pick.markets ?? [];
  return (
    <div className="tn-drawer__scrim" onClick={onClose} role="presentation">
      <aside
        className="tn-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Fair price — ${pick.match_name}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed", top: 0, right: 0, height: "var(--vh-full)", width: "min(440px, 92vw)",
          background: "var(--surface, #0e1116)", borderLeft: "1px solid var(--border, #232a33)",
          boxShadow: "-12px 0 40px rgba(0,0,0,0.45)", zIndex: 1000, overflowY: "auto", padding: "20px 22px",
        }}
      >
        <div className="tn-drawer__head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <Kicker green>STRIKZA Fair Price</Kicker>
            <div className="tn-acca__title" style={{ marginTop: 4 }}>{pick.match_name}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="tn-cta tn-cta--ghost" style={{ cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "14px 0 4px" }}>
          {pick.tier ? <Badge tone={pick.tier === "CONVICTION" ? "tennis" : pick.tier === "LEAN" ? "info" : "muted"}>{pick.tier}</Badge> : null}
          {pick.fair_prob != null ? <Badge tone="muted">model {fmtPct(pick.fair_prob)} · {formatPick(pick.market, pick.selection, pick.match_name)}</Badge> : null}
        </div>

        <div style={{ fontSize: 12, color: "var(--text-secondary)", margin: "10px 0 16px", lineHeight: 1.5 }}>
          Our model&rsquo;s read across every market we could price for this match. <strong>Fair</strong> is our
          probability and price; <strong>Book</strong> is the market line where one exists; <strong>Edge</strong> is
          the gap. A <em>READ</em> means no edge vs the market — our line, shown honestly, not a bet.
        </div>

        {markets.length > 0 ? (
          <div className="tn-drawer__markets" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 0.8fr 0.7fr 0.6fr 0.7fr", gap: 8, fontSize: 11, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: ".04em", paddingBottom: 4, borderBottom: "1px solid var(--border,#232a33)" }}>
              <span>Market</span><span>Fair</span><span>Book</span><span>Edge</span><span>CLV</span>
            </div>
            {markets.map((mk: TennisPickMarket, i: number) => (
              <div key={`${mk.market}-${mk.selection}-${i}`} style={{ display: "grid", gridTemplateColumns: "1.5fr 0.8fr 0.7fr 0.6fr 0.7fr", gap: 8, fontSize: 13, alignItems: "center" }}>
                <span>
                  <span style={{ display: "block" }}>{marketLabel(mk.market)}</span>
                  <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{formatSelection(mk.market, mk.selection, pick.match_name) || "—"}{mk.tier && mk.tier !== "READ" ? ` · ${mk.tier}` : ""}</span>
                </span>
                <span>{fmtPct(mk.fair_prob)}{mk.fair_odds != null ? ` (${mk.fair_odds.toFixed(2)})` : ""}</span>
                <span>{mk.book_odds != null ? mk.book_odds.toFixed(2) : "no line"}</span>
                <span style={{ color: mk.edge_pct != null && mk.edge_pct > 0 && mk.tier !== "READ" ? "var(--green, #3fb950)" : "var(--text-secondary)" }}>
                  {mk.edge_pct != null && mk.tier !== "READ" ? `+${mk.edge_pct.toFixed(1)}%` : "—"}
                </span>
                <span
                  title={mk.entry_odds != null ? `entry ${mk.entry_odds.toFixed(2)}` : undefined}
                  style={{ color: mk.clv_pct != null && mk.clv_pct >= 0 ? "var(--green, #3fb950)" : "var(--text-secondary)" }}
                >
                  {mk.clv_pct != null ? `${mk.clv_pct >= 0 ? "+" : ""}${mk.clv_pct.toFixed(1)}%` : "—"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>Model read only — no market lines posted for this match yet.</div>
        )}
      </aside>
    </div>
  );
}

// The legs list + drawer state. Caller supplies the (already deduped) picks.
export function TennisPickList({ picks }: { picks: TennisPick[] }): JSX.Element {
  const [openPick, setOpenPick] = useState<TennisPick | null>(null);
  return (
    <div className="tn-acca__legs">
      {picks.map((pick, i) => (
        <PickRow key={`${pick.match_name}-${pick.market}-${i}`} pick={pick} onOpen={setOpenPick} />
      ))}
      {openPick ? <FairPriceDrawer pick={openPick} onClose={() => setOpenPick(null)} /> : null}
    </div>
  );
}
